import {
  faBars,
  faCaretDown,
  faChevronLeft,
  faMicrophone,
  faVolumeUp
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DeviceSelect } from '@/components/AudioDeviceSelector/AudioDeviceSelector';
import BetaFeatureSetting from '@/components/BetaFeatureSetting';
import PageHeader from '@/components/PageHeader';
import { BETA_FEATURE_KEYS } from '@/constants/betaFeatures';
import AppContext from '@/contexts/appContext';
import { useNarrowNav, type NarrowNavStyle } from '@/contexts/narrowNavContext';
import { isSoftPhoneActive, useSoftPhone } from '@/contexts/softPhoneContext';
import { DarkStyle, ThemeSetting } from '@/hooks/useThemeSetting';
import { PillSwitch, PillSwitchOption, Select } from '@/ui';
import Container from '@/ui/components/Container';
import InputGroup from '@/ui/components/Form/InputGroup';
import Heading from '@/ui/components/Heading';
import { DARK_STYLE_OPTIONS, THEME_OPTIONS } from '@/utils/theme';

const AppSettings = () => {
  const { themeSetting, setThemeSetting, darkStyle, setDarkStyle, resolvedTheme } =
    useContext(AppContext);
  const { navStyle, setNavStyle } = useNarrowNav();
  const navigate = useNavigate();
  const softPhone = useSoftPhone();
  const [audioLoading, setAudioLoading] = useState(false);

  useEffect(() => {
    if (!isSoftPhoneActive(softPhone)) return;
    setAudioLoading(true);
    softPhone.enumerateDevices().finally(() => setAudioLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container type="page">
      <div className="flex flex-row items-center mb-4">
        <button className="w-6 inline-block text-center mb-1" onClick={() => navigate(-1)}>
          <FontAwesomeIcon data-testid="back-icon" className="text-gray-900" icon={faChevronLeft} />
        </button>

        <div>
          <PageHeader headingText="Dugout Settings" value={1} />
        </div>
      </div>

      <Heading value={2}>Experimental Features</Heading>
      <InputGroup>
        <div className="flex flex-col gap-4">
          <div className="flex w-fit flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Theme</span>
            <PillSwitch
              value={themeSetting}
              onChange={(value) => setThemeSetting(value as ThemeSetting)}>
              {THEME_OPTIONS.map(({ value, label, icon }) => (
                <PillSwitchOption key={value} value={value} icon={icon}>
                  {label}
                </PillSwitchOption>
              ))}
            </PillSwitch>
          </div>

          <div className="flex w-fit flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Navigation</span>
            <PillSwitch value={navStyle} onChange={(value) => setNavStyle(value as NarrowNavStyle)}>
              <PillSwitchOption value="select" icon={faCaretDown}>
                Dropdown
              </PillSwitchOption>
              <PillSwitchOption value="drawer" icon={faBars}>
                Menu
              </PillSwitchOption>
            </PillSwitch>
            <span className="text-xs text-gray-500">
              How you move between pages on narrow screens — the page dropdown Dugout shipped with,
              or the menu behind the top bar&apos;s button. Menu also keeps the top bar, and your
              account menu, at the top on wide screens.
            </span>
          </div>

          {/* Keyed off the resolved theme, so "System" shows it whenever the OS
              is currently dark. */}
          {resolvedTheme === 'dark' && (
            <div className="w-56">
              <Select
                label="Dark style"
                ariaLabel="Dark style"
                options={DARK_STYLE_OPTIONS}
                // There is always a dark style in effect, so clearing it would
                // leave the control empty while the theme stayed put.
                clearable={false}
                selectedOption={
                  DARK_STYLE_OPTIONS.find((option) => option.value === darkStyle) ?? null
                }
                onChange={(option) => option && setDarkStyle(option.value as DarkStyle)}
                helpText={
                  darkStyle === 'midnight'
                    ? 'Near-black surfaces with muted accents.'
                    : 'The original inverted palette.'
                }
              />
            </div>
          )}

          {BETA_FEATURE_KEYS.map((feature) => (
            <BetaFeatureSetting key={feature} feature={feature} />
          ))}
        </div>
      </InputGroup>

      {isSoftPhoneActive(softPhone) && (
        <>
          <Heading value={2} className="mt-6">
            Audio Devices
          </Heading>
          <div className="flex flex-col gap-4 max-w-sm mt-2">
            <DeviceSelect
              label="Microphone"
              icon={faMicrophone}
              devices={softPhone.availableInputDevices}
              selectedId={softPhone.inputDeviceId}
              onChange={(id) => void softPhone.setInputDevice(id)}
              disabled={audioLoading}
            />
            {softPhone.isOutputSelectionSupported && (
              <DeviceSelect
                label="Speaker"
                icon={faVolumeUp}
                devices={softPhone.availableOutputDevices}
                selectedId={softPhone.outputDeviceId}
                onChange={(id) => void softPhone.setOutputDevice(id)}
                disabled={audioLoading}
              />
            )}
          </div>
        </>
      )}
    </Container>
  );
};

export default AppSettings;
